import React, { useEffect, useState, useCallback } from 'react';
import Heading from '../../../components/heading/Heading'; 
import PreLoginLayout from '../components/preloginlayout/PreLoginLayout';
import styles from './SignIn.styles';
import { VEC_ICON_NAME } from '../../../components/icons/constants';
import HoverContainer from '../../../components/hovercontainer/HoverContainer';
import Button from '../../../components/button/Button';
import { BUTTON_TYPE } from '../../../components/button/constants';
import Icons from '../../../components/icons/Icons';
import HorizontalSeparator from '../../../components/separator/horizontalSeparator/HorizontalSeparator';
import { HEADING } from '../../../components/heading/constants/constants';
import TextInput from '../../../components/input/textinput/TextInput';
import PasswordInput from '../../../components/input/passwordinput/PasswordInput';
import { useDispatch, useSelector } from 'react-redux';
import { componentKey, resetSignInPage, setSignInFormFields, setShowTFAScreen, setShowError } from './SignInSlice';
import { componentKey as authenticationComponentKey } from '../../authentication/AuthenticationSlice';
import { getUserGoogleProfile, loginWThirdParty, signInUser, verifyTFACode } from './SignInSaga';
import { useForm, Controller } from "react-hook-form";
import { Link } from 'react-router-dom';
import Loadable from '../../../components/loadable/Loadable';
import Label from '../../../components/label/Label';
import { GoogleLogin, useGoogleLogin } from '@react-oauth/google';
import { PAGE_PATH } from '../../dashboard/constants/constant';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { PRE_LOGIN_LOADING_STATES, PRE_LOGIN_STATES_MESSAGE_MAP, REDIRECT_TO, SSO_TEXTS } from '../constants';
import useGarbageCollector from '../../../../libs/garbagecollector/garbageCollectorHooks';
import General from '../../../../libs/utility/General';
import useTxSearchParams from '../../../../libs/txsearchparams/TxSearchParamsHook';
import useTxNavigate from '../../../../libs/txnavigate/TxNavigateHook';
import { INTERNAL_USER_EMAIL_DOMAINS, PERFTEST_EMAIL_DOMAIN } from '../../../../libs/utility/constants';
import AppleLogin from 'react-apple-login';
import Config from '../../../config.web';
import { FEATURE_FLAG } from '../../../featureFlags';

export default function SignIn() {
    useGarbageCollector(componentKey);

    const { signInFormFields, showError, errorMessage, loadingState, showTFAScreen, userGoogleProfile, signedInUserData } = useSelector((state) => state[componentKey]);
    const { isUserAuthenticated, showSessionError } = useSelector((state) => state[authenticationComponentKey]);
    const { register, control, handleSubmit, formState: {errors}, reset } = useForm();
    const [twoFACode, setTwoFACode] = useState('');
    const [isSignInButtonDisabled, setIsSignInButtonDisabled] = useState(false);
    const [userGoogleTokenObj, setUserGoogleTokenObj] = useState(null);
    const dispatch = useDispatch();
    const navigate = useTxNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [queryParams] = useTxSearchParams();

    const handleReCaptchaVerify = async () => {
        try {
            if (executeRecaptcha) {
                const token = await executeRecaptcha('signin');
                return token;
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleFormSubmit = async (data) => {
        try {
            setIsSignInButtonDisabled(true)
            const gToken = await handleReCaptchaVerify();
            const token = General.handleGRecaptchaToken(gToken)
            if (token) {
                dispatch(signInUser({...signInFormFields, GRecaptchaResponse: token}));
            } else {
                console.error('Error: Unable to generate token.');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsSignInButtonDisabled(false)
        }
    };

    useEffect(() => {
        if (!signedInUserData?.EncUID) {
            return;
        }

        const userEncUID = signedInUserData?.EncUID
        const userDomain = General.getUserDomain(signedInUserData.Email)

        switch (userDomain) {
            case PERFTEST_EMAIL_DOMAIN:
                return
            case INTERNAL_USER_EMAIL_DOMAINS.ZENWORK:
            case INTERNAL_USER_EMAIL_DOMAINS.TAX1099:
                window.userGuiding.identify(userEncUID, { newuser: 'false', internaluser: "true" });
                break
            default:
                window.userGuiding.identify(userEncUID, { newuser: 'false', internaluser: "false" });
                break;
        }
    }, [signedInUserData?.EncUID])

    useEffect(() => {
        if(isUserAuthenticated){
            setShowError({})

            let URL_PATH = queryParams?.get(REDIRECT_TO);

            if(URL_PATH){
                try{
                    URL_PATH =  General.decodeString(URL_PATH);
                }catch(err){
                    console.error(err)
                }
                queryParams?.delete(REDIRECT_TO)
            }
            navigate(URL_PATH || PAGE_PATH.DASHBOARD)
        }
    }, [isUserAuthenticated]);

    useEffect(()=>{
        if(showSessionError){
            dispatch(setShowError({ showError: true, errorMessage:"Your session is timed out or you are not authorized to access" }))
        }
    },[])

    useEffect(() => {
        return () => {
            dispatch(resetSignInPage());
            dispatch(setShowTFAScreen(false))
        };
    }, []);

    useEffect(() => {
        if(userGoogleProfile){
            dispatch(loginWThirdParty({
                IDToken: userGoogleTokenObj?.access_token,
                Email: userGoogleProfile?.email,
                SSOType: "google",
            }));
        }
    }, [userGoogleProfile]);

    const handleInputChange = (e, onChangeValidationController) => {
        const { name, value } = e.target;
        onChangeValidationController(value);
        const formFields = {
            ...signInFormFields,
            [name]: value,
        }
        dispatch(setSignInFormFields(formFields));
    };
    
    const handleTFAChange = (e) => {
        const code = e.target.value
        if(code.length <= 6) {
            setTwoFACode(e.target.value)
        }
    }

    const handleCodeVerification = () => {
        dispatch(verifyTFACode(twoFACode))
    }

    const onGoogleLogin = (response) =>{
        dispatch(loginWThirdParty({
            IDToken: response.credential,
            Email:"",
            SSOType: SSO_TEXTS.GOOGLE_TEXT,
            flag: SSO_TEXTS.SIGNIN_TEXT
        }));
    }

    const onAppleLogin = (response) => {
        if(response?.authorization?.id_token){
            dispatch(loginWThirdParty({
                IDToken: response?.authorization?.id_token,
                Email: "",
                SSOType: SSO_TEXTS.APPLE_TEXT,
                flag: SSO_TEXTS.SIGNIN_TEXT
            }));
        }
    };

    return (
        <>  
            <PreLoginLayout>
                <Loadable
                    currentState={loadingState.state}
                    loadingStates={[PRE_LOGIN_LOADING_STATES.SIGN_IN_PAGE_LOADING, PRE_LOGIN_LOADING_STATES.SSO_SIGN_IN_PAGE_LOADING]}
                    messageMap={PRE_LOGIN_STATES_MESSAGE_MAP}
                    isNewLoadable={true}
                >
                    <div style={styles.loginForm}>
                        <div>
                            { showTFAScreen ? 
                                <div style={styles.TFA_mainContainer}>
                                    <Heading type={HEADING.H1}>Verify Two Factor Authentication</Heading>
                                    <Icons familyName={'vec'} iconName={VEC_ICON_NAME.TWO_FA_VECTOR} style={styles.alignSelfCenter}/>
                                    <div>
                                        <Label customStyles={styles.TFA_Label}>Enter authentication code</Label>
                                        <TextInput type='number' customStyles={styles.textAlignCenter} placeHolder='XXX - XXX' onChangeCb={handleTFAChange} value={twoFACode}/>    
                                    </div>
                                    <Button style={styles.TFA_verifyButton} onClickCb={handleCodeVerification}>Verify Code</Button>
                                </div> :
                                <div>
                                    <form onSubmit={handleSubmit(handleFormSubmit)}>
                                        <Heading type={HEADING.H1}>Sign In</Heading>
                                        <div style={styles.marginTop}>
                                            <Controller
                                                control={control}
                                                name="email"
                                                defaultValue={signInFormFields.email}
                                                rules={{ 
                                                    required: "Email cannot be empty",
                                                    pattern: {
                                                        value: /\S+@\S+\.\S+/,
                                                        message: "Email format is incorrect"
                                                    }
                                                }}
                                                render={({ field : {value, onChange} }) => (
                                                    <TextInput 
                                                        type={'text'}
                                                        placeHolder={'Email'} 
                                                        prefixProps={{ icon: VEC_ICON_NAME.EMAIL_ICON }} 
                                                        name={'email'} 
                                                        value={value} 
                                                        onChangeCb={(e) => handleInputChange(e, onChange)} 
                                                    />
                                                )}
                                            />
                                            {errors.email && <span style={styles.errorMessage}>{errors.email.message}</span>}
                                        </div>
                                        <div style={styles.marginTop}>
                                            <Controller
                                                control={control}
                                                name="password"
                                                defaultValue={signInFormFields.password}
                                                rules={{ 
                                                    required: "Password cannot be empty",
                                                }}
                                                render={({ field : {value, onChange} }) => (
                                                    <PasswordInput 
                                                        name="password" 
                                                        type="password" 
                                                        placeHolder='Password' 
                                                        value={value} 
                                                        onChangeCb={(e) => handleInputChange(e, onChange)} 
                                                    />
                                                )}
                                            />
                                            {errors.password && <span style={styles.errorMessage}>{errors.password.message}</span>}
                                            <HoverContainer style={styles.forgotPasswordDiv}>
                                                <Link style={styles.forgotPasswordLink} to="/forgotpassword">Forgot Password</Link>
                                            </HoverContainer>
                                        </div>
                                        <div style={styles.errorMessageContainer}>
                                        {
                                            showError ? 
                                                <div style={styles.errorMessage}>
                                                    {errorMessage}
                                                </div>
                                            : ""
                                        }    
                                        </div>
                                        <div style={styles.signInButtonDiv}>
                                            <Button type={BUTTON_TYPE.PRIMARY} disabled={isSignInButtonDisabled}>
                                                Sign In
                                            </Button>
                                        </div>
                                    </form>

                                    {FEATURE_FLAG.USER_SSO_LOGIN ? <div style={styles.signWithOptionsMainContainer}>
                                        <p style={styles.signInOptionsTitle}>Sign in with any of these options</p>
                                        <div style={styles.signInOptionsContainer}>
                                            {/* <HoverContainer>
                                                <div style={styles.signInOptionDiv} onClick={googleLogin}>
                                                    <Icons familyName='vec' iconName={VEC_ICON_NAME.GOOGLE_LOGIN_ICON} />
                                                </div>
                                            </HoverContainer> */}
                                            <HoverContainer>
                                                <GoogleLogin
                                                    shape='circle'
                                                    size='large'
                                                    onSuccess={onGoogleLogin}
                                                    onError={(err) => {
                                                        console.log('errror', err)
                                                    }}
                                                    type="icon"
                                                />
                                            </HoverContainer>

                                            {Config.APPLE_LOGIN_CLIENT_ID && Config.APP_BASE_URL ? <HoverContainer>
                                                <AppleLogin
                                                    clientId={Config.APPLE_LOGIN_CLIENT_ID}
                                                    redirectURI={Config.APP_BASE_URL + "/"}
                                                    usePopup={true}
                                                    callback={onAppleLogin}
                                                    scope="email name"
                                                    responseMode="query"
                                                    render={renderProps => (
                                                        <Button prefixProps={{ icon: VEC_ICON_NAME.APPLE_LOGIN_ICON }} style={styles.appleLoginButton} type={BUTTON_TYPE.PRIMARY_LIGHT_WITH_BORDER} onClickCb={renderProps.onClick} />
                                                    )}
                                                />
                                            </HoverContainer> : null}
                                            {/* <HoverContainer>
                                                <div style={styles.signInOptionDiv} onClick={() => {}}>
                                                    <Icons familyName='vec' iconName={VEC_ICON_NAME.GOOGLE_LOGIN_ICON} />
                                                </div>
                                            </HoverContainer>
                                            <HoverContainer>
                                                <div style={styles.signInOptionDiv} onClick={() => {}}>
                                                    <Icons familyName='vec' iconName={VEC_ICON_NAME.QUICK_BOOKS_LOGIN_ICON} />
                                                </div>
                                            </HoverContainer>
                                            <HoverContainer>
                                                <div style={styles.signInOptionDiv} onClick={() => {}}>
                                                    <Icons familyName='vec' iconName={VEC_ICON_NAME.AZURE_LOGIN_ICON} />
                                                </div>
                                            </HoverContainer> */}
                                        </div>
                                    </div> : null}
                                    <div style={styles.separatorDiv}><HorizontalSeparator /></div>
                                    <div style={styles.signUpOptionContainer}>
                                        <span>Don’t have an account?</span>
                                        <div>
                                            <Button type={BUTTON_TYPE.LIGHT_WITH_NO_BORDER} style={{...styles.forgotPasswordLink, ...styles.signUpLink}} onClickCb={() => {navigate('/signup')}} disabled={!FEATURE_FLAG.ALLOW_USER_SIGN_UP}>
                                                Sign Up
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </Loadable>
            </PreLoginLayout>
        </>
    )
}